<script setup lang="ts">
/**
 * Лента заметок выбранной встречи — правая половина экрана «Встречи»
 * (макеты 10-1, 10-2, 10-4).
 *
 * Мысль записывают одним полем: ни проекта, ни типа, ни срока. Каждое поле, без
 * которого не сохранить, — это повод не записать вовсе, а заметку берут посреди
 * разговора, когда думают не про формы.
 *
 * Разбирает мысль ассистент: кнопка «В задачи» открывает панель с готовой
 * командой, а пометку с ключами вышедших задач ставит стор заметок — разговор
 * с ассистентом переживает уход с экрана, и лента его конца не видит. Рядом —
 * «Вручную»: та же заметка телом задачи в обычной форме, новой вкладкой. Форма
 * посреди созвона мешает записывать, поэтому она и уезжает в отдельную вкладку.
 *
 * Законченная встреча — архив: читать можно, дописывать некуда. Протокол,
 * который меняется после конца разговора, перестаёт быть протоколом.
 *
 * На идущей встрече карточку правят вдвоём посимвольно (DEV-1974): у соседа
 * своя каретка, своё выделение и своё имя над ними. Личную заметку и заметку
 * архива правит обычное поле — сливать там нечего.
 */
import { AppUserPicker, useToast } from '@vinprop/design-system'
import { useTextareaAutosize } from '@vueuse/core'
import { ExternalLink, Pencil, Plus, Sparkles, X } from 'lucide-vue-next'
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useIsMobile } from '@/composables/useIsMobile'
import { authApi } from '@/features/auth/api'
import { useAuthStore } from '@/features/auth/store'
import { useAssistantStore } from '@/stores/assistant'

import { isMeetingDone, type MeetingSession } from '../api'
import { peerColor, type PeerState } from '../collab'
import { formatNoteStamp, type Note } from '../notes'
import { useNotesStore } from '../notesStore'
import { useMeetingStore } from '../store'

import MeetingActions from './MeetingActions.vue'
import MeetingCarryOver from './MeetingCarryOver.vue'
import MeetingHeader from './MeetingHeader.vue'
import MeetingTranscript from './MeetingTranscript.vue'
import NoteEditor from './NoteEditor.vue'

import type { Person } from '@/features/auth/types'

const props = defineProps<{
  /** Открытая встреча; пусто — личные заметки (соло-состояние, макет 10-1) */
  meeting: MeetingSession | null
}>()

const { t, locale } = useI18n()
const router = useRouter()
const notes = useNotesStore()
const assistant = useAssistantStore()
const meetingStore = useMeetingStore()
const auth = useAuthStore()
const toast = useToast()
const isMobile = useIsMobile()

/**
 * Поле растёт под текст: на встрече пишут абзацами, а однострочное поле прячет
 * начало мысли, стоит перевалить за строку (DEV-1947). Потолок в десять строк
 * оставляет ленту на экране — дальше поле скроллится внутри себя.
 */
const { textarea: field, input: draft } = useTextareaAutosize()

/**
 * В законченную встречу не дописывают: она архив, а не черновик.
 *
 * Запланированная архивом не считается: повестку пишут до созвона, и поле для
 * неё обязано стоять на месте (макет 12-3).
 */
const isArchive = computed(() => !!props.meeting && isMeetingDone(props.meeting))

/** Идущая встреча, открытая прямо сейчас, — та, где работают вдвоём */
const isLive = computed(() => !!props.meeting?.active)

const stamp = (note: Note) =>
  formatNoteStamp(note.createdAt, locale.value, {
    today: t('meetings.today'),
    yesterday: t('meetings.yesterday'),
  })

/**
 * Записать заметку.
 *
 * Поле чистится только после того, как сервер её принял. Иначе оборвавшаяся
 * сеть или закрытая соседом встреча съедали бы набранный текст молча — а на
 * созвоне это единственная копия только что сказанного.
 */
const save = async () => {
  const text = draft.value.trim()

  if (!text) return

  const saved = await notes.add(text)

  meetingStore.setPresence({ typing: false })

  if (saved) {
    draft.value = ''

    return
  }

  toast.error(t('meetings.notes.saveFailed'))
}

/** Кого зовут на встречу: справочник людей — право `users:read` */
const people = ref<Person[]>([])
const isDirectoryOpen = ref(true)
const invitee = ref('')

/**
 * Счётчик перерисовок пикера.
 *
 * Приглашение — действие, а не поле: после него в строке не должно оставаться
 * имени позванного, иначе следующего зовут поверх чужого текста. Сброса
 * `modelValue` для этого мало — своё состояние поиска компонент держит сам.
 */
const pickerKey = ref(0)

const loadPeople = async () => {
  if (people.value.length || !isDirectoryOpen.value) return

  try {
    people.value = await authApi.listUsers()
  } catch {
    // Без права на справочник звать некого — пикер честно говорит об этом, а
    // не притворяется пустым списком.
    isDirectoryOpen.value = false
  }
}

/**
 * Позвать человека. Первое приглашение и заводит встречу — до него заметки
 * личные, и задним числом они общими не становятся.
 */
const invite = async (userId: string) => {
  if (!userId) return

  invitee.value = ''
  pickerKey.value += 1
  await meetingStore.invite([userId])
}

/** Кто из соседей что делает прямо сейчас (DEV-1974) */
const peers = computed<PeerState[]>(() => meetingStore.peers)

/** Соседи, правящие эту же карточку */
const peersOf = (noteId: string) => peers.value.filter((peer) => peer.noteId === noteId)

/**
 * Кто печатает новую заметку — ту, которой ещё нет.
 *
 * Показывается призрачной карточкой под полем: без неё двое пишут одно и то же
 * дважды, каждый в свою заметку, и узнают об этом уже в протоколе.
 */
const typingPeers = computed(() => peers.value.filter((peer) => peer.typing && !peer.noteId))

/** Заметку правят вдвоём только на идущей встрече: в архиве сливать нечего */
const isSharedEdit = computed(() => isLive.value)

/** Пока связь потеряна, свои правки копятся локально и уйдут пачкой */
const isPendingLocally = computed(() => meetingStore.isOffline)

/**
 * Пишем в поле — соседи видят «печатает заметку…».
 *
 * Гасим по паузе, а не по потере фокуса: человек отвлёкся на разговор, а
 * призрак у соседа продолжал бы обещать заметку, которой не будет.
 */
let typingTimer: ReturnType<typeof setTimeout> | null = null

const onComposerInput = () => {
  if (!isLive.value) return

  meetingStore.setPresence({ typing: true, noteId: '' })

  if (typingTimer) clearTimeout(typingTimer)

  typingTimer = setTimeout(() => meetingStore.setPresence({ typing: false }), 1_500)
}

/** Правка заметки прямо в карточке */
const editingId = ref('')
const editDraft = ref('')
const isSavingEdit = ref(false)

/**
 * Можно ли править эту заметку.
 *
 * Свою — всегда: описка в собственной мысли правится там, где её видно, и
 * заводить ради этого встречу незачем. Бэкенд это и разрешал, а экран запрещал
 * (DEV-1949).
 *
 * Чужую — пока идёт та встреча, где её записали. Считается по встрече самой
 * заметки, а не по тому, есть ли у смотрящего какая-нибудь открытая: в общей
 * ленте рядом лежат заметки разных встреч, и «у меня сейчас идёт мит» ничего не
 * говорит о чужом завершённом протоколе.
 */
const canEdit = (note: Note): boolean => {
  // Личная заметка в ленте всегда своя: чужих личных туда не приезжает.
  if (!note.sessionId) return true

  if (note.authorId === auth.user?.id) return true

  return meetingStore.isActive && meetingStore.session?.id === note.sessionId
}

const startEdit = (note: Note) => {
  if (!canEdit(note)) return

  editingId.value = note.id
  editDraft.value = note.body
}

/**
 * Применить правку. Поле закрывается только после ответа сервера: закрыв его
 * раньше, отказ пришлось бы показывать поверх текста, которого уже нет.
 */
const applyEdit = async () => {
  const id = editingId.value

  if (!id || isSavingEdit.value) return

  isSavingEdit.value = true

  const saved = await notes.edit(id, editDraft.value)

  isSavingEdit.value = false

  if (saved) {
    editingId.value = ''

    return
  }

  toast.error(t('meetings.notes.saveFailed'))
}

/** Отменить правку: текст возвращается к тому, что на сервере */
const cancelEdit = () => {
  editingId.value = ''
}

/**
 * Закончить правку с телефона.
 *
 * Совместная правка уже сохранена — её пишет документ встречи по ходу набора;
 * обычному полю нужен ещё запрос. Разница снаружи не видна и видна быть не
 * должна: кнопка одна и означает «я дописал».
 */
const finishEdit = () => {
  if (isSharedEdit.value) {
    cancelEdit()

    return
  }

  void applyEdit()
}

const canRemove = (note: Note): boolean => !note.sessionId || canEdit(note)

/**
 * Enter сохраняет, Shift+Enter переносит строку.
 *
 * Кнопки «сохранить» нет: на встрече пишут вслепую, слушая разговор, и
 * дотягиваться до кнопки после каждой мысли значит потерять следующую.
 */
const onEnter = (event: KeyboardEvent) => {
  if (event.shiftKey) return

  event.preventDefault()
  void save()
}

const focus = async () => {
  await nextTick()
  field.value?.focus()
}

/**
 * Потолок текста заметки в команде: длинный протокол незачем гнать в промпт
 * целиком, но обрыв на первой строке — это уже не заметка.
 */
const PROMPT_LIMIT = 4_000

/**
 * Разобрать заметку. Команда уходит в панель готовой (паттерн `commands.ts`):
 * человек нажал кнопку у конкретной карточки, и переспрашивать его «какую
 * именно» — значит заставить пересказать то, что он уже показал пальцем.
 *
 * Текст уезжает целиком, а не приметой в 80 знаков. По примете ассистент
 * разыскивал заметку сам — перебирал задачи и заметки вместо того, чтобы
 * разбирать (DEV-1949), и тратил на поиски половину потолка вызовов.
 */
const parse = (note: Note) => {
  notes.startParsing(note.id)
  assistant.open()

  void assistant.send(
    t('meetings.notes.parse.prompt', { text: note.body.trim().slice(0, PROMPT_LIMIT) }),
  )
}

/**
 * Завести задачу из заметки руками, без ассистента.
 *
 * Новой вкладкой: форма — это разговор на несколько полей, а на созвоне
 * записывают дальше, и подменять ленту формой значит прервать запись ради
 * оформления. Тело заметки станет телом задачи, заголовком — её первая строка.
 */
const openIssueForm = (note: Note) => {
  const href = router.resolve({ name: 'plan', query: { note: note.id } }).href

  window.open(href, '_blank', 'noopener')
}

/**
 * Лента: конспект ИИ первой карточкой, следом человеческие заметки.
 *
 * Конспект наверху, а не по времени: он про весь созвон целиком, и человек,
 * открывший встречу назавтра, приходит именно за ним. Действия из расшифровки
 * идут своей секцией — у каждого свои кнопки и своё состояние.
 */
const feed = computed(() => [
  ...notes.items.filter((note) => note.kind === 'ai_summary'),
  ...notes.items.filter((note) => note.kind === 'human'),
])

const actions = computed(() => notes.items.filter((note) => note.kind === 'ai_action'))

/**
 * Когда был прошлый созвон серии.
 *
 * Берётся из уже загруженного списка встреч, а не отдельным запросом: список
 * на экране всё равно есть, и лишний поход за одной датой не окупается.
 */
const previousAt = computed(() => {
  const previousId = props.meeting?.carryOver?.previousSessionId ?? ''
  const previous = meetingStore.meetings.find((meeting) => meeting.id === previousId)

  return previous?.scheduledStart || previous?.createdAt || ''
})

const hasSummary = computed(() => notes.items.some((note) => note.kind === 'ai_summary'))

/** Запись ещё едет из Google: конспекта нет, но он будет (макет 12-5) */
const isProcessing = computed(
  () => ['ENDED', 'PROCESSING'].includes(props.meeting?.meetingStatus ?? '') && !hasSummary.value,
)

/**
 * Разбирать ассистентом карточку ИИ незачем: её текст модель уже написала, и
 * второй проход по нему — это разбор разбора (решение владельца). «Вручную»
 * остаётся везде: иногда нужна ровно одна задача и ровно этими словами.
 */
const canParse = (note: Note) => note.kind === 'human'

/**
 * Расшифровка показывается у прошедшего созвона с комнатой: у ад-хок встречи
 * без Meet записывать было нечего, а у идущей запись ещё не доехала.
 */
const hasTranscript = computed(
  () =>
    !!props.meeting?.meetingId &&
    ['READY', 'QUALIFIED', 'NO_RECORDING'].includes(props.meeting.meetingStatus),
)

/** Говорящих модель угадала — предупреждение живёт в самой расшифровке */
const speakersInferred = computed(() =>
  notes.items.some((note) => note.ai?.speakersInferred ?? false),
)

defineExpose({ focus })

// Экран открывают ради того, чтобы писать: курсор уже в поле. В архиве писать
// некуда — там фокус только сбил бы прокрутку к концу протокола.
watch(
  () => props.meeting?.id ?? '',
  () => {
    if (!isArchive.value) void focus()
  },
  { immediate: true },
)
</script>

<template>
  <section class="notes" :class="{ 'notes--mobile': isMobile }">
    <!-- Шапка встречи живёт своим компонентом: состояний у неё шесть, и у
         каждого свои полосы и свои кнопки (DEV-2100). Личным заметкам ничего
         этого не нужно — они не встреча. -->
    <MeetingHeader v-if="meeting" :meeting="meeting" />

    <template v-else>
      <header class="notes__head">
        <h2 class="notes__title">{{ t('meetings.personal.title') }}</h2>

        <button
          type="button"
          class="notes__icon"
          :title="t('meetings.notes.add')"
          :aria-label="t('meetings.notes.add')"
          @click="focus()"
        >
          <Plus :size="16" />
        </button>
      </header>

      <!-- Первое приглашение и заводит встречу: до него заметки личные. -->
      <div class="meet">
        <span class="meet__label">{{ t('meetings.session.solo') }}</span>

        <div class="meet__actions">
          <AppUserPicker
            :key="pickerKey"
            v-model="invitee"
            class="meet__picker"
            :users="people"
            :placeholder="t('meetings.session.invite')"
            :empty-text="t('meetings.session.nobody')"
            :unavailable="!isDirectoryOpen"
            :unavailable-text="t('meetings.session.noDirectory')"
            @click="loadPeople()"
            @update:model-value="invite"
          />
        </div>
      </div>
    </template>

    <!-- Серия начинается не с чистого листа: нерешённое с прошлого созвона
         стоит в повестке следующего (макет 12-7). -->
    <MeetingCarryOver
      v-if="meeting?.carryOver"
      :carry-over="meeting.carryOver"
      :previous-at="previousAt"
    />

    <!-- Связь потеряна: правки не пропадают, но и к соседям пока не едут.
         Молчать об этом нельзя — экран выглядел бы живым. -->
    <p v-if="isPendingLocally" class="notes__pending">{{ t('meetings.session.pending') }}</p>

    <div v-if="!isArchive" class="notes__field">
      <textarea
        ref="field"
        v-model="draft"
        class="notes__input"
        :placeholder="t('meetings.notes.placeholder')"
        :aria-label="t('meetings.notes.placeholder')"
        @input="onComposerInput"
        @keydown.enter="onEnter"
      />

      <p class="notes__keys">{{ t('meetings.notes.keys') }}</p>
    </div>

    <article v-for="peer in typingPeers" :key="peer.userId" class="note note--ghost">
      <p class="note__body note__body--ghost">
        {{ t('meetings.session.typing', { name: peer.name }) }}
      </p>
    </article>

    <p v-if="notes.isLoaded && !notes.items.length" class="notes__empty">
      {{ isArchive ? t('meetings.notes.emptyArchive') : t('meetings.notes.empty') }}
    </p>

    <!-- Протокол длиннее ленты: сказать об этом честнее, чем показать кусок
         встречи как целое. -->
    <p v-else-if="notes.isTruncated" class="notes__empty">
      {{ t('meetings.notes.truncated', { shown: notes.items.length, total: notes.total }) }}
    </p>

    <!-- Правка на телефоне занимает экран целиком (макет 10-11): в карточке
         высотой в три строки не видно ни того, что правишь, ни того, что рядом
         пишет сосед. Тап по подложке закрывает — Esc на телефоне нет. -->
    <div v-if="isMobile && editingId" class="notes__backdrop" @click="editingId && applyEdit()" />

    <!-- Обработка идёт часами: место конспекта занимает его скелетон, чтобы
         экран не выглядел так, будто разбора не будет вовсе (макет 12-5). -->
    <div v-if="isProcessing" class="skeleton" :aria-label="t('meetings.ai.waiting')">
      <span class="skeleton__line skeleton__line--title" />
      <span class="skeleton__line" />
      <span class="skeleton__line skeleton__line--short" />
    </div>

    <!-- Конспекта нет — обработка сорвалась, а действия остались: тогда секция
         открывает ленту, иначе она стоит сразу под конспектом. -->
    <MeetingActions v-if="!hasSummary" :actions="actions" :project-id="meeting?.projectId ?? ''" />

    <template v-for="note in feed" :key="note.id">
      <article
        class="note"
        :class="{
          'note--shared': peersOf(note.id).length,
          'note--fullscreen': isMobile && editingId === note.id,
        }"
      >
        <!-- Бейдж в углу, а не флажок у каретки: флажок перекрывал первую строку
           текста ровно там, где её и читают. -->
        <span
          v-for="peer in peersOf(note.id)"
          :key="peer.userId"
          class="note__peer"
          :style="{ background: peerColor(peer.userId) }"
        >
          {{ t('meetings.session.editing', { name: peer.name }) }}
        </span>

        <!-- Совместная правка: текст держит документ встречи, каретка соседа
           видна прямо в поле (DEV-1974). -->
        <NoteEditor
          v-if="editingId === note.id && isSharedEdit"
          :note-id="note.id"
          :body="note.body"
          :peers="peersOf(note.id)"
          @close="cancelEdit()"
        />

        <!-- Личная заметка и архив: обычное поле, сливать там нечего. -->
        <textarea
          v-else-if="editingId === note.id"
          v-model="editDraft"
          class="note__edit"
          rows="3"
          :aria-label="t('meetings.notes.edit')"
          @keydown.enter.exact.prevent="applyEdit()"
          @keydown.esc.stop="cancelEdit()"
          @blur="applyEdit()"
        />

        <p v-else class="note__body" :class="{ 'note__body--processed': note.processedAt }">
          {{ note.body }}
        </p>

        <!-- Выйти из полноэкранной правки: тап по подложке работает, но кнопка
           не требует целиться в полоску по краю экрана. -->
        <button
          v-if="isMobile && editingId === note.id"
          type="button"
          class="note__done-edit"
          @click="finishEdit()"
        >
          {{ t('meetings.notes.done') }}
        </button>

        <div class="note__meta">
          <span class="note__time">{{ stamp(note) }}</span>

          <!-- Подпись автора — только у сессионной: в личной ленте автор один. -->
          <span v-if="note.sessionId && note.authorName" class="note__author">
            {{ note.authorName }}
          </span>

          <!-- Конспект написала модель, и подпись участника под ним была бы
             неправдой: автор карточки — служебный «Ассистент» (DEV-2096). -->
          <span v-if="note.kind === 'ai_summary'" class="note__ai">
            <Sparkles :size="11" aria-hidden="true" />
            {{ t('meetings.ai.badge') }}
          </span>

          <template v-if="note.processedAt">
            <span class="note__done">✓ {{ t('meetings.notes.processed') }}</span>

            <RouterLink
              v-for="key in note.issueKeys"
              :key="key"
              class="note__chip"
              :to="`/issue/${key}`"
            >
              {{ key }}
            </RouterLink>
          </template>

          <template v-else>
            <button v-if="canParse(note)" type="button" class="note__parse" @click="parse(note)">
              <Sparkles :size="12" aria-hidden="true" />
              {{ t('meetings.notes.parse.action') }}
            </button>

            <!-- Та же заметка, но обычной формой и без ассистента: он умеет
               разобрать мысль на несколько задач, а иногда нужна ровно одна и
               ровно этими словами. -->
            <button
              type="button"
              class="note__manual"
              :title="t('meetings.notes.manual.hint')"
              @click="openIssueForm(note)"
            >
              <ExternalLink :size="12" aria-hidden="true" />
              {{ t('meetings.notes.manual.action') }}
            </button>
          </template>
        </div>

        <!-- Кнопки появляются по наведению и раскладку не двигают: правка
           случается чаще всего прочего, но глаз ей занимать незачем. -->
        <div v-if="editingId !== note.id" class="note__tools">
          <button
            v-if="canEdit(note)"
            type="button"
            class="note__tool"
            :title="t('meetings.notes.edit')"
            :aria-label="t('meetings.notes.edit')"
            @click="startEdit(note)"
          >
            <Pencil :size="13" />
          </button>

          <button
            v-if="canRemove(note)"
            type="button"
            class="note__tool note__remove"
            :title="t('meetings.notes.remove')"
            :aria-label="t('meetings.notes.remove')"
            @click="notes.remove(note.id)"
          >
            <X :size="14" />
          </button>
        </div>
      </article>

      <!-- Сначала что было, потом что с этим делать (макет 12-6). -->
      <MeetingActions
        v-if="note.kind === 'ai_summary'"
        :actions="actions"
        :project-id="meeting?.projectId ?? ''"
      />
    </template>

    <MeetingTranscript
      v-if="hasTranscript && meeting"
      :session-id="meeting.id"
      :speakers-inferred="speakersInferred"
    />
  </section>
</template>

<style scoped>
.notes {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 14px;
  /* Лента скроллится внутри себя: длинный протокол не должен тянуть страницу —
     колонка встреч слева обязана остаться на месте. */
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
}

.notes__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.note__ai {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px;
  border-radius: var(--radius-pill, 999px);
  background: var(--color-primary-tint);
  color: var(--color-primary);
  font-size: var(--font-size-overline);
  line-height: 18px;
}

.skeleton {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

.skeleton__line {
  height: 10px;
  border-radius: 5px;
  background: var(--color-bg-muted);
}

.skeleton__line--title {
  width: 40%;
  height: 14px;
}

.skeleton__line--short {
  width: 60%;
}

.notes__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: 20px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.2px;
  line-height: 26px;
}

.notes__mode {
  color: var(--color-primary);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

/* Законченная встреча подписана приглушённо: это состояние, а не действие */
.notes__mode--past {
  color: var(--color-text-muted);
}

.notes__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: auto;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.notes__icon:hover {
  color: var(--color-text-primary);
}

.notes__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border: 1px solid var(--color-primary);
  /* 12 из макета: между --radius-md (10) и --radius-lg (16) токена нет */
  border-radius: 12px;
  background: var(--color-surface);
}

.notes__input {
  width: 100%;
  /* Пять строк минимум, десять максимум: ниже пяти поле прячет начало мысли,
     выше десяти — выталкивает ленту за экран (DEV-1947). */
  min-height: 100px;
  max-height: 200px;
  overflow-y: auto;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--font-size-body);
  line-height: 20px;
  resize: none;
}

.notes__input:focus {
  outline: none;
}

.notes__input::placeholder {
  color: var(--color-text-muted);
}

/* Ряд встречи: кто на ней, кого позвать, чем закончить */
.meet {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding-bottom: 2px;
}

.meet__label {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.meet__person {
  padding: 2px 8px;
  border-radius: 10px;
  background: var(--color-primary-tint);
  color: var(--color-primary);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

/* Позвали, но человек ещё не пришёл: приглашение ушло, участником он не стал */
.meet__person--waiting {
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
}

/* В архиве участники — история, а не присутствие: цвет живого мита им не идёт */
.meet__person--past {
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
}

/* Связь оборвалась: чужие заметки сейчас не приходят, и молчать об этом
   нельзя — пустая лента выглядит как «никто ничего не пишет». */
.meet__offline {
  color: var(--color-danger);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.meet__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.meet__picker {
  min-width: 200px;
}

.meet__end {
  padding: 4px 10px;
  white-space: nowrap;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: none;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-caption);
  line-height: 16px;
  cursor: pointer;
}

.meet__end:hover {
  border-color: var(--color-danger);
  color: var(--color-danger);
}

/* Правка обязана быть видна: без рамки поле неотличимо от текста, и человек не
   понимает, правит он или читает. */
.note__edit {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--font-size-body);
  line-height: 20px;
  resize: none;
}

.note__edit:focus {
  outline: none;
}

.note__author {
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

.notes__keys {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.notes__empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

/* Карточку правит сосед: рамка цветом встречи, чтобы её было видно в ленте */
.note--shared {
  border-color: var(--color-primary);
}

/* Призрак: заметки ещё нет, но сосед её пишет */
.note--ghost {
  border-style: dashed;
  background: none;
}

.note__body--ghost {
  color: var(--color-text-muted);
  font-style: italic;
}

.note__peer {
  position: absolute;
  top: -9px;
  right: 12px;
  padding: 1px 6px;
  border-radius: 8px;
  color: #fff;
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

.notes__pending {
  margin: 0;
  padding: 8px 12px;
  border-radius: var(--radius-sm);
  background: var(--color-warning-tint);
  color: var(--color-warning-strong);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.meet__dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 4px;
  border-radius: 50%;
  vertical-align: middle;
}

.note {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

.note__body {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  line-height: 20px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* Уголок под кнопки: они всплывают по наведению поверх текста и съедали конец
   первой строки. Обтекание, а не отступ, — тогда место теряет одна строка, а
   не весь абзац. */
.note__body::before {
  float: right;
  width: 52px;
  height: 20px;
  content: '';
}

/* Разобранная заметка остаётся в ленте, но уступает свежим */
.note__body--processed {
  color: var(--color-text-secondary);
}

.note__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.note__time {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.note__done {
  margin-left: auto;
  color: var(--color-success-strong);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

.note__chip {
  padding: 2px 6px;
  border-radius: 5px;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  text-decoration: none;
}

.note__parse,
.note__manual {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  padding: 4px 8px;
  /* 6 — радиус чипа из макета; токена ровно на него нет, ближайшие 8 и 10
     заметно круглее на кнопке высотой 24. */
  border: none;
  border-radius: 6px;
  background: var(--color-primary-tint);
  color: var(--color-primary);
  font-family: inherit;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  cursor: pointer;
}

/* Ручное создание — соседняя кнопка, а не вторая главная: вправо пару отжимает
   только первая, и звать глазом должна тоже она. */
.note__manual {
  margin-left: 0;
  background: var(--color-surface-variant);
  color: var(--color-text-muted);
}

.note__manual:hover {
  color: var(--color-text);
}

.note__tools {
  position: absolute;
  top: 8px;
  right: 8px;
  display: none;
  gap: 2px;
}

.note__tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-muted);
  cursor: pointer;
}

.note__tool:hover {
  color: var(--color-text);
}

.note__remove:hover {
  color: var(--color-danger);
}

.note:hover .note__tools,
.note__tools:focus-within {
  display: flex;
}

.note__done-edit {
  align-self: flex-end;
  padding: 6px 14px;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
}

/* На телефоне наведения нет: кнопки, которые появляются по нему, там просто
   не существуют — а правка нужна ровно так же. */
.notes--mobile .note__tools {
  display: flex;
}

@media (hover: none) {
  .note__tools {
    display: flex;
  }
}

.notes__backdrop {
  position: fixed;
  inset: 0;
  z-index: 30;
  background: var(--color-background-overlay);
}

/* Правка на телефоне: карточка поверх всего, лента остаётся за подложкой.
   Сверху остаётся полоса подложки — по ней закрывают тапом. */
.note--fullscreen {
  position: fixed;
  inset: 12vh 12px 12px;
  z-index: 31;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
}

.note--fullscreen :deep(.live__input),
.note--fullscreen .note__edit {
  min-height: 40vh;
}
</style>
